import { VestingChart } from '@dealstack/frontend/shared/components/VestingChart/VestingChart';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { numberToUiPercent } from '@dealstack/shared/lib/format/number';
import { GetSecurityVestingRes } from '@dealstack/shared/lib/generated';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { Flex, Loader, Text } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	vestingQuery: UseQueryResult<{ payload?: GetSecurityVestingRes } | undefined>;
	tFunction: TranslateFunction<
		I18Namespaces.CAP_MANAGE | I18Namespaces.INVESTOR | I18Namespaces.MIP_MANAGE
	>;
	tooltipTranslationKey: string;
	xLabel: string;
	yLabel: string;
}

export const VestingExpandedContent = ({
	vestingQuery,
	tFunction,
	tooltipTranslationKey,
	xLabel,
	yLabel,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const { data, isLoading } = vestingQuery;
	const vestingData = data?.payload;

	if (isLoading) {
		return (
			<Flex justify="center" mih="2rem">
				<Loader />
			</Flex>
		);
	}

	if (!vestingData?.chartData?.length) {
		return (
			<Flex justify="center" p="md">
				<Text c="dimmed">{tCommonUi('no_vesting_data')}</Text>
			</Flex>
		);
	}

	const vestedPercentage =
		vestingData.vestedPercentage !== undefined
			? numberToUiPercent(vestingData.vestedPercentage, true)
			: undefined;

	return (
		<>
			<Flex
				justify="start"
				align="center"
				mb="1rem"
				gap="0.25rem"
				data-testid={GENERIC_ELEMENT_ID.VESTED_PERCENTAGE_TEXT}
			>
				<Text fz="lg" fw={400}>
					{tCommonUi('vested_percentage_text')}
				</Text>
				<Text fz="lg" fw={600}>
					{vestedPercentage}
				</Text>
			</Flex>

			<VestingChart
				chartData={vestingData.chartData ?? []}
				vestingTerms={vestingData.vestingTerms}
				earliestTsaDate={vestingData.earliestTsaDate}
				tFunction={tFunction}
				tooltipTranslationKey={tooltipTranslationKey}
				xLabel={xLabel}
				yLabel={yLabel}
			/>
		</>
	);
};
